import type { RouteLeg, RouteObligation, RoutePlanStop } from '@/types/route-plan';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';

export type RouteScanRequest = {
  date: string;
  weekday: string;
  windowStart?: string | null;
  windowEnd?: string | null;
  familyMembers: { id: string; name: string; notes: string[] }[];
  userNotes: string[];
  meFamilyMemberId?: string | null;
};

export type RouteBuildRequest = {
  date: string;
  weekday: string;
  homeAddress: string;
  country?: string | null;
  locale?: string | null;
  currentTime?: string | null;
  stops: RoutePlanStop[];
  familyMemberNames: Record<string, string>;
};

/**
 * RoutePlanApiClient — typed client for the route planner AI endpoints.
 * Failures throw with the server's error message; there is no fallback
 * route generation — callers surface an explicit error state with Retry.
 */
export class RoutePlanApiClient {
  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await authenticatedFetch.fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      throw new Error(
        payload.error ?? payload.message ?? `Request failed: ${response.status}`,
      );
    }

    return response.json() as Promise<T>;
  }

  /**
   * Scan family notes for obligations on the given day.
   */
  async scan(request: RouteScanRequest): Promise<RouteObligation[]> {
    const data = await this.post<{ obligations: RouteObligation[] }>(
      '/api/route-plan/scan',
      request,
    );
    return data.obligations;
  }

  /**
   * Build ordered route legs from confirmed stops.
   */
  async build(request: RouteBuildRequest): Promise<RouteLeg[]> {
    const data = await this.post<{ legs: RouteLeg[] }>(
      '/api/route-plan/build',
      request,
    );
    return data.legs;
  }
}

export const routePlanApiClient = new RoutePlanApiClient();
